/**
 * ScheduleModal — multi-step scheduling flow with AI assistance.
 *
 * Three modes for picking when to publish:
 *   1. Plain-English (agentic) — type intent, AI parses into a recurring plan
 *   2. AI Best-Times — system recommends optimal slots per channel
 *   3. Manual — pick exact date/time
 *
 * Mocked end-to-end. Output is a "schedule preview" the user can confirm to
 * queue the item into Approvals + Calendar.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Sparkles, Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight,
  Wand2, RotateCcw, CheckCircle2, Loader2, Repeat, Zap, TrendingUp, Hash, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL_BEST_TIMES = {
  linkedin: [{ day: "Tue", time: "9:00 AM", score: 92 }, { day: "Wed", time: "11:30 AM", score: 88 }, { day: "Thu", time: "2:00 PM", score: 85 }],
  email:    [{ day: "Tue", time: "10:00 AM", score: 91 }, { day: "Fri", time: "10:00 AM", score: 89 }, { day: "Thu", time: "8:00 AM", score: 84 }],
  instagram:[{ day: "Wed", time: "11:30 AM", score: 90 }, { day: "Fri", time: "5:00 PM", score: 87 }, { day: "Sun", time: "1:00 PM", score: 82 }],
  x:        [{ day: "Mon", time: "8:00 AM", score: 86 }, { day: "Wed", time: "12:00 PM", score: 84 }, { day: "Thu", time: "5:00 PM", score: 81 }],
  blog:     [{ day: "Wed", time: "8:00 AM", score: 95 }, { day: "Tue", time: "9:30 AM", score: 90 }, { day: "Mon", time: "8:00 AM", score: 86 }],
};

// Naive NL → schedule parser (mocked Schedule Agent).
function parseUtterance(text) {
  const t = text.toLowerCase();
  let cadence = "once", interval = 1, duration = null;

  if (/every (\d+) ?(week|wk)/.test(t)) { const m = t.match(/every (\d+) ?(week|wk)/); cadence = "weekly"; interval = parseInt(m[1]); }
  else if (/(weekly|every week)/.test(t)) { cadence = "weekly"; interval = 1; }
  else if (/(bi.?weekly|every two weeks|every 2 weeks)/.test(t)) { cadence = "weekly"; interval = 2; }
  else if (/(monthly|every month)/.test(t)) { cadence = "monthly"; }
  else if (/(daily|every day)/.test(t)) { cadence = "daily"; }

  if (/1 quarter|one quarter|q[1-4]/.test(t)) duration = "1 quarter (12 weeks)";
  else if (/(\d+) ?month/.test(t)) { const m = t.match(/(\d+) ?month/); duration = `${m[1]} months`; }
  else if (/(\d+) ?week/.test(t)) { const m = t.match(/(\d+) ?week/); duration = `${m[1]} weeks`; }
  else if (/this week|next week/.test(t)) duration = "next 7 days";

  const channels = [];
  if (/linkedin/i.test(t)) channels.push("linkedin");
  if (/instagram|ig\b/i.test(t)) channels.push("instagram");
  if (/email|newsletter/i.test(t)) channels.push("email");
  if (/twitter|x\.com/i.test(t)) channels.push("x");
  if (/blog|article|post/i.test(t)) channels.push("blog");
  if (channels.length === 0) channels.push("linkedin", "email");

  let campaignName = "Untitled Campaign";
  const cm = text.match(/(?:schedule|launch|run|publish)\s+(?:the\s+)?([A-Z0-9][\w\s&-]{2,40})\s+(?:campaign|push|series|launch)/i);
  if (cm) campaignName = cm[1].trim();

  return { campaignName, cadence, interval, duration, channels, raw: text };
}

function expandSchedule(plan) {
  const slots = [];
  const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const totalWeeks = plan.duration === "1 quarter (12 weeks)" ? 12 : plan.duration?.match(/(\d+)/) ? Math.min(parseInt(plan.duration.match(/(\d+)/)[1]) * (plan.duration.includes("month") ? 4 : 1), 12) : 4;

  const today = new Date();
  for (let i = 0; i < totalWeeks; i += plan.interval || 1) {
    plan.channels.forEach((ch) => {
      const best = (CHANNEL_BEST_TIMES[ch] || [])[0];
      if (!best) return;
      const slotDate = new Date(today);
      slotDate.setDate(today.getDate() + (i * 7) + ((dayMap[best.day] - today.getDay() + 7) % 7));
      slots.push({
        channel: ch,
        date: slotDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        time: best.time,
        score: best.score,
        weekIdx: i,
      });
    });
  }
  return slots.slice(0, 24);
}

const CHANNEL_LABEL = { linkedin: "LinkedIn", email: "Email", instagram: "Instagram", x: "X / Twitter", blog: "Blog" };
const CHANNEL_COLOR = { linkedin: "#0077B5", email: "#2E63B8", instagram: "#E4405F", x: "#0f1419", blog: "#1B365D" };

export default function ScheduleModal({ open, onClose, item }) {
  const [mode, setMode] = useState("ai-nl");
  const [utterance, setUtterance] = useState("Schedule the Echo EHR campaign to run every 2 weeks for 1 quarter on LinkedIn and email");
  const [plan, setPlan] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [manual, setManual] = useState({ date: "", time: "09:00", recurring: false });

  useEffect(() => { if (!open) { setPlan(null); setMode("ai-nl"); } }, [open]);

  const runParser = () => {
    if (!utterance.trim()) return;
    setParsing(true);
    setTimeout(() => {
      const parsed = parseUtterance(utterance);
      setPlan({ ...parsed, slots: expandSchedule(parsed) });
      setParsing(false);
    }, 1100);
  };

  const confirm = () => {
    toast.success(plan
      ? `${plan.campaignName} scheduled · ${plan.slots.length} sends queued in Approvals`
      : "Scheduled · queued in Approvals");
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="schedule-dialog" data-testid="schedule-modal">
        <DialogTitle style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
          Schedule
        </DialogTitle>
        <div className="schedule-shell">
          {/* Header */}
          <div className="schedule-header">
            <div>
              <h2 className="schedule-title">Schedule {item?.title || "this asset"}</h2>
              <p className="schedule-sub">When should this go out? AI will pick optimal times unless you override.</p>
            </div>
          </div>

          {/* Mode picker */}
          <div className="mkt-segmented" style={{ marginBottom: 16 }}>
            {[
              { k: "ai-nl", l: "AI · Plain English", icon: Wand2 },
              { k: "ai-best", l: "AI · Best times", icon: TrendingUp },
              { k: "manual", l: "Manual", icon: CalendarIcon },
            ].map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.k} onClick={() => { setMode(m.k); setPlan(null); }} className={mode === m.k ? "active" : ""} data-testid={`sched-mode-${m.k}`}>
                  <Icon style={{ width: 11, height: 11, marginRight: 4, display: "inline" }} /> {m.l}
                </button>
              );
            })}
          </div>

          {/* AI · Plain English */}
          {mode === "ai-nl" && !plan && (
            <div>
              <div className="ai-banner" style={{ marginBottom: 14 }}>
                <div className="ai-pill"><Sparkles style={{ width: 11, height: 11 }} /> Schedule Agent</div>
                <p style={{ fontSize: 12.5 }}>
                  Type what you want in plain English. The agent parses cadence, duration, channels, and picks the optimal time per platform.
                </p>
              </div>
              <textarea
                className="input schedule-nl-input"
                rows={3}
                value={utterance}
                onChange={(e) => setUtterance(e.target.value)}
                placeholder="e.g. Schedule the EHR campaign to run every 2 weeks for 1 quarter on LinkedIn and email"
                data-testid="sched-nl-input"
              />
              <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
                {[
                  "Every Tuesday morning for the next 6 weeks across LinkedIn and email",
                  "Daily Instagram stories for the next 14 days",
                  "Monthly newsletter on the first Tuesday for 1 quarter",
                ].map((s, i) => (
                  <button key={i} onClick={() => setUtterance(s)} className="tag tag-gray" style={{ cursor: "pointer", border: "0.5px solid var(--border)" }}>{s}</button>
                ))}
              </div>
              <button onClick={runParser} className="btn btn-primary" style={{ marginTop: 14, width: "100%" }} disabled={parsing} data-testid="sched-nl-parse">
                {parsing
                  ? <><Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite" }} /> Parsing intent…</>
                  : <><Wand2 style={{ width: 13, height: 13 }} /> Generate schedule</>}
              </button>
            </div>
          )}

          {/* AI · Best times */}
          {mode === "ai-best" && (
            <div>
              <div className="ai-banner" style={{ marginBottom: 14 }}>
                <div className="ai-pill"><TrendingUp style={{ width: 11, height: 11 }} /> Send-time optimization</div>
                <p style={{ fontSize: 12.5 }}>
                  These are the highest-engagement windows from your audience's historical activity. Click a slot to schedule.
                </p>
              </div>
              {Object.entries(CHANNEL_BEST_TIMES).map(([ch, times]) => (
                <div key={ch} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: CHANNEL_COLOR[ch] }} />
                    <span style={{ fontSize: 12, fontWeight: 580, color: "var(--navy)" }}>{CHANNEL_LABEL[ch]}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {times.map((t, i) => (
                      <button key={i} onClick={confirm}
                        className="mkt-card is-interactive"
                        style={{ flex: 1, padding: "10px 12px", textAlign: "left", cursor: "pointer", border: i === 0 ? "1px solid var(--blue)" : "0.5px solid var(--border)" }}
                        data-testid={`best-${ch}-${i}`}>
                        <div style={{ fontSize: 13, fontWeight: 580 }}>{t.day} · {t.time}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                          <span className="num" style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>Score</span>
                          <span className="num" style={{ fontSize: 12, fontWeight: 600, color: "var(--success)" }}>{t.score}</span>
                        </div>
                        {i === 0 && <span className="tag tag-blue" style={{ marginTop: 6, display: "inline-block" }}>Recommended</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Manual */}
          {mode === "manual" && (
            <div>
              <label className="cs-label">Date</label>
              <input type="date" className="input" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} data-testid="sched-manual-date" />
              <label className="cs-label">Time (ET)</label>
              <input type="time" className="input" value={manual.time} onChange={(e) => setManual({ ...manual, time: e.target.value })} data-testid="sched-manual-time" />
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }}>
                <input type="checkbox" checked={manual.recurring} onChange={(e) => setManual({ ...manual, recurring: e.target.checked })} />
                <Repeat style={{ width: 13, height: 13 }} /> Recurring
              </label>
              <button onClick={confirm} className="btn btn-primary" style={{ marginTop: 18, width: "100%" }} data-testid="sched-manual-confirm">
                <CheckCircle2 style={{ width: 13, height: 13 }} /> Schedule
              </button>
            </div>
          )}

          {/* Plan preview */}
          {plan && (
            <div data-testid="schedule-preview">
              <div className="schedule-plan-summary">
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <Zap style={{ width: 13, height: 13, color: "var(--blue)" }} />
                  <strong>{plan.campaignName}</strong>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 11.5, color: "var(--text-secondary)" }}>
                  <span><Repeat style={{ width: 11, height: 11, display: "inline" }} /> {plan.cadence === "weekly" ? `Every ${plan.interval} week${plan.interval > 1 ? "s" : ""}` : plan.cadence}</span>
                  <span>·</span>
                  <span><Clock style={{ width: 11, height: 11, display: "inline" }} /> {plan.duration || "one-time"}</span>
                  <span>·</span>
                  <span><Hash style={{ width: 11, height: 11, display: "inline" }} /> {plan.channels.length} channel{plan.channels.length > 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span className="num">{plan.slots.length} sends</span>
                </div>
              </div>

              <h4 className="schedule-preview-h">Sends preview</h4>
              <div className="schedule-slot-grid">
                {plan.slots.slice(0, 12).map((s, i) => (
                  <div key={i} className="schedule-slot">
                    <span className="schedule-slot-ch" style={{ background: CHANNEL_COLOR[s.channel] }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 580 }}>{CHANNEL_LABEL[s.channel]}</div>
                      <div className="num" style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>{s.date} · {s.time}</div>
                    </div>
                    <span className="num tag tag-green" style={{ fontSize: 10 }}>{s.score}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={() => setPlan(null)} className="btn" style={{ flex: 1 }}>
                  <RotateCcw style={{ width: 13, height: 13 }} /> Re-do
                </button>
                <button onClick={confirm} className="btn btn-primary" style={{ flex: 2 }} data-testid="sched-confirm">
                  <CheckCircle2 style={{ width: 13, height: 13 }} /> Confirm · queue {plan.slots.length} sends <ArrowRight style={{ width: 12, height: 12 }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
